import { getApiBaseUrl } from "../utils/getApiBaseUrl";

const API_BASE_URL = getApiBaseUrl();

/** Sales-readiness is optional; never block the public catalog on this endpoint. */
const OSHAR_SALES_READINESS_TIMEOUT_MS = 7000;
/** Public tier catalog must not hang the page in preview/fallback forever. */
const OSHAR_TIERS_TIMEOUT_MS = 15000;

const SALES_READINESS_FALLBACK = {
  success: false,
  ready: false,
  tiers: [],
  blockers: [],
  warnings: [],
  meta: {},
};

async function fetchJsonWithTimeout(url, timeoutMs = OSHAR_SALES_READINESS_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal: controller.signal,
    });
    const data = await res.json().catch(() => ({}));
    return { res, data };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Public tiers + inventory from TreydoraEdBackend (no auth).
 * @returns {Promise<{ tiers: object[], meta: object | null }>}
 */
export async function getOsharRealEstateTiers() {
  const url = `${API_BASE_URL.replace(/\/$/, "")}/api/oshar-real-estate/tiers`;
  const bad = (tiersRequest) => ({ tiers: [], meta: null, tiersRequest });
  try {
    const { res, data } = await fetchJsonWithTimeout(url, OSHAR_TIERS_TIMEOUT_MS);
    if (!res.ok) {
      return bad({
        ok: false,
        httpStatus: res.status,
        errorKey: "http_error",
        url,
      });
    }
    if (Array.isArray(data?.tiers) && data.tiers.length > 0) {
      return {
        tiers: data.tiers,
        meta: data.meta || null,
        tiersRequest: { ok: true, httpStatus: res.status, errorKey: "", url },
      };
    }
    if (!data?.success || !Array.isArray(data.tiers)) {
      return bad({
        ok: false,
        httpStatus: res.status,
        errorKey: "invalid_payload",
        url,
      });
    }
    return {
      tiers: data.tiers,
      meta: data.meta || null,
      tiersRequest: { ok: true, httpStatus: res.status, errorKey: "", url },
    };
  } catch (e) {
    const timedOut = e?.name === "AbortError";
    return bad({
      ok: false,
      httpStatus: null,
      errorKey: timedOut ? "timeout" : "network",
      url,
      networkMessage: String(e?.message || "fetch_failed"),
    });
  }
}

/**
 * Public read-only: next wallet-mintable token in tier Stripe unit range (no auth).
 * @returns {Promise<{
 *   success?: boolean,
 *   ready: boolean,
 *   tokenId: number | null,
 *   tierSlug: string | null,
 *   priceWei: string | null,
 *   chainId: number | null,
 *   contractAddress: string | null,
 *   imageUrl: string | null,
 *   metadataStatus: string | null,
 *   blockers: string[]
 * }>}
 */
export async function getOsharWalletMintCandidate(tierSlug) {
  const slug = String(tierSlug || "").trim();
  if (!slug) {
    return {
      success: false,
      ready: false,
      tokenId: null,
      tierSlug: null,
      priceWei: null,
      chainId: null,
      contractAddress: null,
      imageUrl: null,
      metadataStatus: null,
      blockers: ["tierSlug is required."],
    };
  }
  const url = `${API_BASE_URL.replace(/\/$/, "")}/api/oshar-real-estate/wallet-mint-candidate?tierSlug=${encodeURIComponent(
    slug
  )}`;
  try {
    const res = await fetch(url, { method: "GET", headers: { Accept: "application/json" } });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        ready: false,
        tokenId: null,
        tierSlug: slug,
        priceWei: null,
        chainId: data?.chainId ?? null,
        contractAddress: data?.contractAddress ?? null,
        imageUrl: null,
        metadataStatus: null,
        blockers: [data?.message || "Unable to load wallet mint candidate."],
      };
    }
    return {
      success: data?.success !== false,
      ready: Boolean(data?.ready),
      tokenId: data?.tokenId != null ? Number(data.tokenId) : null,
      tierSlug: data?.tierSlug != null ? String(data.tierSlug) : slug,
      priceWei: data?.priceWei != null ? String(data.priceWei) : null,
      chainId: data?.chainId != null ? Number(data.chainId) : null,
      contractAddress: data?.contractAddress != null ? String(data.contractAddress) : null,
      imageUrl: data?.imageUrl != null ? String(data.imageUrl) : null,
      metadataStatus: data?.metadataStatus != null ? String(data.metadataStatus) : null,
      blockers: Array.isArray(data?.blockers) ? data.blockers.map(String) : [],
    };
  } catch {
    return {
      success: false,
      ready: false,
      tokenId: null,
      tierSlug: slug,
      priceWei: null,
      chainId: null,
      contractAddress: null,
      imageUrl: null,
      metadataStatus: null,
      blockers: ["Unable to reach the Treydora API. Check your network and REACT_APP_API_BASE_URL."],
    };
  }
}

/**
 * Public Stripe fulfillment readiness (no auth).
 * @returns {Promise<{ success: boolean, ready?: boolean, tiers?: object[], blockers?: string[], warnings?: string[], meta?: object }>}
 */
export async function getOsharRealEstateSalesReadiness() {
  const url = `${API_BASE_URL.replace(/\/$/, "")}/api/oshar-real-estate/sales-readiness`;
  try {
    const { res, data } = await fetchJsonWithTimeout(url);
    if (!res.ok || data?.success === false) {
      return {
        ...SALES_READINESS_FALLBACK,
        blockers: Array.isArray(data?.blockers) ? data.blockers : [],
      };
    }
    return {
      success: true,
      ready: Boolean(data?.ready),
      tiers: Array.isArray(data?.tiers) ? data.tiers : [],
      blockers: Array.isArray(data?.blockers) ? data.blockers : [],
      warnings: Array.isArray(data?.warnings) ? data.warnings : [],
      meta: data?.meta && typeof data.meta === "object" ? data.meta : {},
    };
  } catch (e) {
    const timedOut = e?.name === "AbortError";
    if (timedOut && process.env.NODE_ENV === "development") {
      console.warn(
        `[oshar-real-estate] sales-readiness timed out after ${OSHAR_SALES_READINESS_TIMEOUT_MS}ms; live tier catalog unchanged.`
      );
    } else if (process.env.NODE_ENV === "development") {
      console.warn("[oshar-real-estate] sales-readiness unavailable; live tier catalog unchanged.", e?.message || e);
    }
    return { ...SALES_READINESS_FALLBACK };
  }
}

/** @deprecated Prefer getOsharRealEstateTiers */
export async function fetchPublicOsharRealEstateTiers() {
  const { tiers } = await getOsharRealEstateTiers();
  return tiers;
}
